import axios, { type AxiosInstance } from "axios";
import { baseURL } from "@/contexts/AxiosContext";

/** Shared by every api/*.ts domain module - kept separate from api.ts to avoid circular imports between domains. */

// Kept for non-hook usage (e.g. scripts/tests). App code should prefer `useAxios()`.
export function createApiClient(): AxiosInstance {
  return axios.create({
    baseURL
  });
}

// Re-exported for backwards compatibility - the actual (framework-free) implementations
// live in "@/api/errors" so services/entityOpsV2.ts can depend on them without pulling in
// "@/contexts/AxiosContext" (React + okta-react). Prefer importing from "@/api/errors" directly
// in new framework-free service modules; existing api/*.ts call sites can keep using this path.
export { getErrorMessage, ProjectLockedError, isProjectLockedError, throwLockAwareError } from "@/api/errors";

export interface ProjectSaveConflictEntity {
  readonly type: string;
  readonly id: string;
}

export interface ProjectSaveConflictResponseBody {
  readonly error?: string;
  readonly message?: string;
  readonly conflicting_entities?: readonly ProjectSaveConflictEntity[];
}

export class ProjectSaveConflictError extends Error {
  readonly conflictingEntities: readonly ProjectSaveConflictEntity[];

  constructor(message: string, conflictingEntities: readonly ProjectSaveConflictEntity[]) {
    super(message);
    this.name = "ProjectSaveConflictError";
    this.conflictingEntities = conflictingEntities;
  }
}

/** `"character" | "location" | "shot"` — the mutable entity types sharing lock/reorder shapes. */
export type MutableEntityType = "character" | "location" | "shot";

/** PATCH reorder body for character, location, shot (zero-based index). */
export type MutableEntityReorderBody = Readonly<{ position: number }>;
